import { useParams } from 'react-router-dom';
import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApolloError, gql, useQuery, useMutation } from '@apollo/client';
import { useForm, useFormContext, FormProvider, Controller } from 'react-hook-form';
import { string, object } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'

import Select from 'components/global/CUK/AsyncSelect/Select'
import { toast } from 'components/global/CUK/Toast/Toast';
import { trackEvent } from 'analytics';

import TextField from 'packages/ui/TextField';
import { deserializeErrors } from 'apollo-client';

import {Header} from 'components/Modal';
import { useRoom } from '../../use-rooms';

const OPERATION_FIND_MODELS = gql`
  query FindModelsByManufacturer($manufacturer: String!) {
    models: findModelsByManufacturer(manufacturer: $manufacturer) {
      uuid
      name
    }
  }
`

const OPERATION_REPLACE = gql`
  mutation ReplaceTvByNewDevice($sf_id: String!, $device_uuid: String!, $model_uuid: String!, $model_name: String!, $serial: String!) {
    device: replaceTvByNewDevice(sf_id: $sf_id, device_uuid: $device_uuid, model_uuid: $model_uuid, model_name: $model_name, serial: $serial) {
      ... on TV {
        uuid
      }
    }
  }
`

const MANUFACTURERS = ['LG', 'Philips', 'Samsung']

const buildSchema = ({t}) => {
  return object({
    manufacturer: string().required(t('ui_elements.required_field')),
    model: object({
      id: string().required(t('ui_elements.required_field')),
      name: string().required(t('ui_elements.required_field')),
    }).required(t('ui_elements.required_field')),
    serial: string().required(t('ui_elements.required_field')),
  })
}

const Manufacturer = () => {
  const { control, setValue } = useFormContext()
  const { t } = useTranslation();
  return (
    <Stack>
      <Typography component="span" sx={{ fontSize: 12, color: 'content.secondary' }}>{t('stream-cast.actions.replace_device.manufacturer_title')}</Typography>
      <Controller
        name="manufacturer"
        control={control}
        render={({ field }) => (
          <Select
            /* @ts-expect-error not jsx */
            value={field.value ? { id: field.value, name: field.value } : null}
            onChange={newValue => {
              setValue('model', null)
              field.onChange(newValue.id)
            }}
            options={MANUFACTURERS.map((name) => ({ id: name, name }))}
            defaultPlaceholder={t('ui_elements.select')}
            noOptionsMessage={t('stream-cast.actions.replace.no_options_spares')}
            hideSelectedOptions={true}
          />
        )}
      />
      
    </Stack>
  )
}

const Model = () => {
  const { t } = useTranslation();
  const { control, watch, formState: { errors } } = useFormContext()
  const manufacturer = watch('manufacturer')
  const { data, loading, error } = useQuery(OPERATION_FIND_MODELS, {
    variables: { manufacturer },
    skip: !manufacturer,
  });

  return (
    <Stack>
      <Typography component="span" sx={{ fontSize: 12, color: 'content.secondary' }}>{t('stream-cast.actions.replace_device.model_title')}</Typography>
      {
        // @ts-expect-error from JSX
        Boolean(error) && <Typography variant="caption" color="error">{deserializeErrors(error as ApolloError)}</Typography>
      }
      <Controller
        name="model"
        control={control}
        render={({ field }) => (
          <Select
            key={manufacturer}
            /* @ts-expect-error not jsx */
            menuHeight="140px"
            menuWidth="100%"
            isSearchable={true}
            value={field.value}
            onChange={field.onChange}
            options={loading || error || !data ? [] : data.models.map(model => ({ id: model.uuid, name: model.name }))}
            defaultPlaceholder={loading ? t('ui_elements.loading') : t('ui_elements.select')}
            noOptionsMessage={t('stream-cast.actions.replace.no_options_model')}
            hideSelectedOptions={true}
          />
        )}
      />
      {
        errors.model && <Typography variant="caption" color="error">{t('ui_elements.required_field')}</Typography>
      }
    </Stack>
  )
}

const NewDevice = ({ onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const room = useRoom()
  const { deviceId } = useParams();
  const [replaceDevice, { loading }] = useMutation(OPERATION_REPLACE, {
    onCompleted: (data) => {
      trackEvent('stream_cast_device_replaced', { replacement: 'new_device', device_kind: 'tv' });
      toast.success(t('stream-cast.actions.replace.toast_success'));
      onSuccess(data.device.uuid);
    },
    onError: () => {
      trackEvent('stream_cast_action_failed', { action: 'replace', replacement: 'new_device', device_kind: 'tv' });
      toast.error(t('stream-cast.actions.replace.toast_error'));
    },
    refetchQueries: ['GetRooms']
  });
  const formMethods = useForm({
    defaultValues: {
      manufacturer: 'LG',
      model: null,
      serial: '',
    },
    resolver: yupResolver(buildSchema({ t }))
  })

  const handleSubmit = async ({ model, serial }) => {
    await replaceDevice({
      variables: {
        sf_id: room?.site_sf_id,
        device_uuid: deviceId,
        model_uuid: model.id,
        model_name: model.name,
        serial: serial,
      },
    })
  }
  
  return (
    <Stack gap={2} sx={{ height: '100%' }}>
      <Header icon={<i className="fa-light fa-box-taped" />} title={t('stream-cast.actions.replace_device.title')} />
      <Stack justifyContent="space-between" sx={{ height: '100%' }}>
        <Stack gap={1}>
          <Typography component="span" sx={{ fontSize: 12, textAlign: 'center' }}>
            {t('stream-cast.actions.replace_device.description')}
          </Typography>
          <FormProvider {...formMethods}>
            <Manufacturer />
            <Model />
            <Stack>
              <TextField
                name="serial"
                label={t('stream-cast.actions.replace_device.serial_title')}
                placeholder={t('ui_elements.to_input')} />
            </Stack>
          </FormProvider>
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Button variant="outlined" onClick={onCancel} size="small">
          {t('stream-cast.actions.cancel')}
        </Button>
        <Button variant="contained" color="primary" disabled={loading} onClick={formMethods.handleSubmit(handleSubmit)} size="small">
          {t('stream-cast.actions.submit')}
        </Button>
      </Stack>
    </Stack>
  )
}

export default NewDevice;